import { useEffect, useState, type ReactNode } from 'react';
import { Mail, MessageSquare, Users, Upload, Send, CalendarClock, FileText, type LucideIcon } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  useCreateCampaign,
  useUploadCampaignRecipients,
  useScheduleCampaign,
  useSendCampaign,
  type CreateCampaignInput,
  type UploadResult,
} from '@/hooks/customer-engagement/use-campaigns';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import { parseRecipientFile, type ParseRecipientFileResult } from '@/lib/recipient-file-parser';
import { CAMPAIGN_SMS_MAX_CHARS, countSmsSegments } from '@/lib/sms-segments';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultChannel?: 'email' | 'sms';
}

// Standard form tokens — align with the Create Customer redesign. Text/number
// inputs use the shared <Input>; these tokens cover the controls that aren't
// <Input> (native <select>, the textareas).
const inputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const textareaBase = 'w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-1';
const textareaNormal = `${textareaBase} border-input focus:border-primary focus:ring-primary`;
const textareaError = `${textareaBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

type Delivery = 'now' | 'later' | 'draft';

// Schedule timezones, Pacific first (the platform default). Values are IANA
// zones, which are DST-aware — "Pacific" is PST in winter and PDT in summer
// automatically, so the operator never has to think about daylight saving.
const SCHEDULE_TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/Los_Angeles', label: 'Pacific — PT (PST / PDT)' },
  { value: 'America/Denver', label: 'Mountain — MT (MST / MDT)' },
  { value: 'America/Chicago', label: 'Central — CT (CST / CDT)' },
  { value: 'America/New_York', label: 'Eastern — ET (EST / EDT)' },
  { value: 'UTC', label: 'UTC' },
];
const DEFAULT_TIMEZONE = 'America/Los_Angeles';

type FormState = {
  name: string;
  channel: 'email' | 'sms';
  audienceType: 'all' | 'manual_list';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  delivery: Delivery;
  timezone: string;
  scheduledAt: string;
  targetFinishAt: string;
};

function emptyForm(channel: 'email' | 'sms', canSend = true): FormState {
  return {
    name: '',
    channel,
    audienceType: 'all',
    subject: '',
    bodyHtml: '',
    bodyText: '',
    // Without Send and schedule a campaign can only be saved as a draft.
    delivery: canSend ? 'now' : 'draft',
    timezone: DEFAULT_TIMEZONE,
    scheduledAt: '',
    targetFinishAt: '',
  };
}

// Offset (ms) of an IANA time zone from UTC at a given instant. Positive = ahead
// of UTC. Uses Intl so it's correct across DST boundaries with no dependencies.
function tzOffsetMs(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(map.year, map.month - 1, map.day, map.hour % 24, map.minute, map.second);
  return asUtc - at.getTime();
}

// Interpret a datetime-local string ("YYYY-MM-DDTHH:mm") as wall-clock time in
// `timeZone` and return the corresponding UTC ISO string. DST-aware: the offset
// is resolved for that specific date, so Pacific 9:00 AM is PDT in July, PST in
// January. This is what makes "the time you pick" mean the same thing no matter
// what timezone the operator's own computer happens to be set to.
function zonedLocalToIsoUtc(local: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local ?? '');
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const wallAsUtc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  const offset = tzOffsetMs(timeZone, new Date(wallAsUtc));
  const instant = wallAsUtc - offset;
  return Number.isNaN(instant) ? null : new Date(instant).toISOString();
}

// datetime-local string ("YYYY-MM-DDTHH:mm", wall-clock in `timeZone`) → the
// corresponding Date instant, for feeding the DatePicker (which is pinned to the
// same zone so it renders the exact wall clock the operator picked).
function zonedLocalToDate(local: string, timeZone: string): Date | null {
  const iso = zonedLocalToIsoUtc(local, timeZone);
  return iso ? new Date(iso) : null;
}

// Inverse of zonedLocalToDate: a Date instant → the "YYYY-MM-DDTHH:mm" wall-clock
// string in `timeZone`. Keeps the stored value in the exact shape the schedule
// logic (zonedLocalToIsoUtc) already expects, so the timezone dropdown keeps
// meaning the wall clock unchanged.
function instantToZonedLocalInput(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
}

// Render a UTC ISO string in a given zone, e.g. "Jul 3, 2026, 9:00 AM PDT".
// Uses explicit date/time components rather than dateStyle/timeStyle: older
// engines (e.g. the Chromium in some embedded webviews) reject combining
// `timeZoneName` with dateStyle/timeStyle and throw "Invalid option", which
// would crash the schedule preview.
function formatInTz(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

// Preview of the exact SMS that goes out. The message is sent as-typed (the
// merchant's own brand lives in the body) — no platform prefix. We only append
// an opt-out line if the operator didn't include one, mirroring the send worker.
function wrapWithCompliance(body: string): string {
  let out = body;
  if (!/STOP/i.test(out)) out = `${out} Reply STOP to unsubscribe.`;
  return out;
}

export function CampaignCreateDialog({ open, onClose, defaultChannel = 'email' }: Props) {
  const canSend = usePermissions().has('campaigns.send');
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultChannel, canSend));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [parseResult, setParseResult] = useState<ParseRecipientFileResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const create = useCreateCampaign();
  const upload = useUploadCampaignRecipients();
  const schedule = useScheduleCampaign();
  const send = useSendCampaign();

  const { selectedLocationId } = useLocationStore();

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm(defaultChannel, canSend));
    setFieldErrors({});
    setParseResult(null);
    setUploadResult(null);
  }, [open, defaultChannel, canSend]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((e) => {
        const rest = { ...e };
        delete rest[key as string];
        return rest;
      });
    }
  };

  const onFilePicked = async (file: File | null) => {
    if (!file) {
      setParseResult(null);
      return;
    }
    setParsing(true);
    setParseResult(null);
    try {
      const result = await parseRecipientFile(file);
      setParseResult(result);
      if (result.rowsWithPhone === 0) {
        toast.error('No phone numbers detected. Make sure the file has a column named "phone".');
      } else {
        toast.success(
          `Parsed ${result.totalRows.toLocaleString()} rows · ${result.rowsWithPhone.toLocaleString()} phone numbers detected`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Could not read file: ${msg}`);
    } finally {
      setParsing(false);
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Campaign name is required';
    if (form.channel === 'email') {
      if (!form.subject.trim()) errors.subject = 'Subject is required for email';
      if (!form.bodyHtml.trim()) errors.bodyHtml = 'HTML body is required for email';
    } else {
      if (!form.bodyText.trim()) errors.bodyText = 'Message body is required for SMS';
      const wrapped = wrapWithCompliance(form.bodyText.trim());
      if (wrapped.length > CAMPAIGN_SMS_MAX_CHARS) {
        errors.bodyText = `Keep the message under ${CAMPAIGN_SMS_MAX_CHARS} characters — currently ${wrapped.length} (including the auto-added "Reply STOP" line if any)`;
      }
    }
    if (form.audienceType === 'manual_list') {
      if (!parseResult) errors.audienceFile = 'Upload a CSV or Excel file';
      else if (parseResult.rowsWithPhone === 0) errors.audienceFile = 'File has no phone numbers';
    }
    if (form.delivery === 'later') {
      const startIso = zonedLocalToIsoUtc(form.scheduledAt, form.timezone);
      const finishIso = zonedLocalToIsoUtc(form.targetFinishAt, form.timezone);
      if (!startIso) errors.scheduledAt = 'Pick a start date and time';
      if (!finishIso) errors.targetFinishAt = 'Pick a finish-by date and time';
      if (startIso && finishIso && new Date(finishIso).getTime() <= new Date(startIso).getTime()) {
        errors.targetFinishAt = 'Finish-by must be after start';
      }
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Guard against an accidental immediate blast — confirm "Send now".
    if (form.delivery === 'now') {
      const count =
        form.audienceType === 'manual_list' && parseResult
          ? `${parseResult.rowsWithPhone.toLocaleString()} uploaded recipient(s)`
          : 'all opted-in customers at this location';
      if (!window.confirm(`Send "${form.name.trim()}" now to ${count}? This cannot be undone.`)) return;
    }

    setSubmitting(true);
    try {
      const createPayload: CreateCampaignInput = {
        name: form.name.trim(),
        channel: form.channel,
        audienceType: form.audienceType,
        locationId: selectedLocationId || undefined,
        ...(form.channel === 'email'
          ? { subject: form.subject.trim(), bodyHtml: form.bodyHtml.trim() }
          : { bodyText: form.bodyText.trim() }),
      };
      const created = await create.mutateAsync(createPayload);
      const campaignId = created.data.id;

      if (form.audienceType === 'manual_list' && parseResult) {
        const result = await upload.mutateAsync({ id: campaignId, recipients: parseResult.rows });
        setUploadResult(result.data);
      }

      if (form.delivery === 'now') {
        // Fire immediately. SMS routes through the paced worker (starts now);
        // email sends inline. Either way the list's Progress column tracks it.
        await send.mutateAsync(campaignId);
        toast.success(`Campaign "${form.name.trim()}" is sending now — track it in the campaigns list.`);
      } else if (form.delivery === 'later') {
        const startIso = zonedLocalToIsoUtc(form.scheduledAt, form.timezone)!;
        const finishIso = zonedLocalToIsoUtc(form.targetFinishAt, form.timezone)!;
        await schedule.mutateAsync({ id: campaignId, scheduledAt: startIso, targetFinishAt: finishIso });
        toast.success(
          `Campaign "${form.name.trim()}" scheduled — sending starts ${formatInTz(startIso, form.timezone)}.`,
        );
      } else {
        toast.success(`Campaign "${form.name.trim()}" saved as draft. Use "Send Now" in the list when ready.`);
      }
      onClose();
    } catch (err) {
      const msg = (err as { error?: { message?: string } })?.error?.message ?? (err as Error)?.message ?? String(err);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const channelSms = form.channel === 'sms';
  const composedSms = channelSms ? wrapWithCompliance(form.bodyText.trim()) : '';
  const smsSegments = countSmsSegments(composedSms);

  // Resolved schedule instants (for the "server sees" helper line). Computed in
  // the operator-chosen zone so the preview matches exactly what gets stored.
  const scheduleStartIso = form.delivery === 'later' ? zonedLocalToIsoUtc(form.scheduledAt, form.timezone) : null;
  const scheduleFinishIso = form.delivery === 'later' ? zonedLocalToIsoUtc(form.targetFinishAt, form.timezone) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Compose Campaign"
      widthClass="max-w-[820px]"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {form.audienceType === 'manual_list' && parseResult && (
              <span>
                {parseResult.rowsWithPhone.toLocaleString()} of {parseResult.totalRows.toLocaleString()} rows have a
                phone number.
              </span>
            )}
            {uploadResult && (
              <span>
                Upload: matched {uploadResult.matched.toLocaleString()}, enrolled{' '}
                {uploadResult.enrolled.toLocaleString()}, skipped{' '}
                {(
                  uploadResult.skipped.duplicate +
                  uploadResult.skipped.invalidPhone +
                  uploadResult.skipped.optOut
                ).toLocaleString()}
                .
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" form="campaign-create-form" disabled={submitting}>
              {submitting
                ? 'Working…'
                : form.delivery === 'now'
                  ? 'Send Now'
                  : form.delivery === 'later'
                    ? 'Save & Schedule'
                    : 'Save as Draft'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="campaign-create-form" onSubmit={handleSubmit} noValidate className="space-y-7">
        {/* ── 1 · basics (name + channel + audience) ───────────────── */}
        <FormSection num={1} title="Basics">
          <div>
            <Label htmlFor="campaign-name">
              Campaign name (internal) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="campaign-name"
              className="mt-1"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Friday morning blast"
              error={!!fieldErrors.name}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
          </div>

          <div>
            <Label>Channel</Label>
            <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <PickerCard
                selected={form.channel === 'email'}
                onClick={() => set('channel', 'email')}
                icon={Mail}
                label="Email"
                description="Rich HTML message"
              />
              <PickerCard
                selected={form.channel === 'sms'}
                onClick={() => set('channel', 'sms')}
                icon={MessageSquare}
                label="SMS"
                description="Short text message"
              />
            </div>
          </div>

          <div>
            <Label>Audience</Label>
            <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <PickerCard
                selected={form.audienceType === 'all'}
                onClick={() => set('audienceType', 'all')}
                icon={Users}
                label={`All ${channelSms ? 'SMS' : 'email'} opt-in`}
                description="Everyone opted in at this location"
              />
              <PickerCard
                selected={form.audienceType === 'manual_list'}
                onClick={() => set('audienceType', 'manual_list')}
                icon={Upload}
                label="Manual list"
                description="Upload a CSV / Excel file"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {form.audienceType === 'all'
                ? `Only customers with ${channelSms ? 'sms_opt_in' : 'email_opt_in'} = true at this location receive the message.`
                : 'Upload a list (CSV or Excel) with a "phone" column. Unknown numbers are auto-enrolled with sms_opt_in=true (campaign_upload). Opted-out existing customers are respected.'}
            </p>
          </div>

          {form.audienceType === 'manual_list' && (
            <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4">
              <Label htmlFor="campaign-file">
                Customer file <span className="text-red-500">*</span>
              </Label>
              <input
                id="campaign-file"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm"
              />
              {parsing && <p className="mt-2 text-xs text-muted-foreground">Parsing file…</p>}
              {parseResult && (
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p>
                    Format: <span className="font-medium">{parseResult.format.toUpperCase()}</span> · Rows:{' '}
                    <span className="font-medium">{parseResult.totalRows.toLocaleString()}</span> · With phone:{' '}
                    <span className="font-medium text-foreground">{parseResult.rowsWithPhone.toLocaleString()}</span>
                    {parseResult.rowsWithoutPhone > 0 && (
                      <>
                        {' '}
                        · Skipped (no phone):{' '}
                        <span className="font-medium">{parseResult.rowsWithoutPhone.toLocaleString()}</span>
                      </>
                    )}
                  </p>
                  <p>
                    Detected columns — phone:{' '}
                    <code className="rounded bg-muted px-1">{parseResult.detectedColumns.phone ?? '(none)'}</code>
                    {parseResult.detectedColumns.firstName && (
                      <>
                        , first: <code className="rounded bg-muted px-1">{parseResult.detectedColumns.firstName}</code>
                      </>
                    )}
                    {parseResult.detectedColumns.lastName && (
                      <>
                        , last: <code className="rounded bg-muted px-1">{parseResult.detectedColumns.lastName}</code>
                      </>
                    )}
                    {parseResult.detectedColumns.email && (
                      <>
                        , email: <code className="rounded bg-muted px-1">{parseResult.detectedColumns.email}</code>
                      </>
                    )}
                  </p>
                </div>
              )}
              {fieldErrors.audienceFile && <p className="mt-2 text-xs text-red-500">{fieldErrors.audienceFile}</p>}
            </div>
          )}
        </FormSection>

        {/* ── 2 · message ──────────────────────────────────────────── */}
        <FormSection num={2} title="Message">
          {channelSms ? (
            <div>
              <Label htmlFor="campaign-sms-body">
                SMS body <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="campaign-sms-body"
                value={form.bodyText}
                onChange={(e) => set('bodyText', e.target.value)}
                rows={4}
                placeholder="Friday 20% off everything in store!"
                className={`mt-1 ${fieldErrors.bodyText ? textareaError : textareaNormal}`}
              />
              <div className="mt-1 flex items-center justify-between text-xs">
                <span
                  className={composedSms.length > CAMPAIGN_SMS_MAX_CHARS ? 'text-red-500' : 'text-muted-foreground'}
                >
                  Composed: {composedSms.length}/{CAMPAIGN_SMS_MAX_CHARS} chars ·{' '}
                  <span className={smsSegments.segments > 1 ? 'font-medium text-amber-600' : undefined}>
                    {smsSegments.segments} {smsSegments.segments === 1 ? 'segment' : 'segments'}
                  </span>
                  {smsSegments.encoding === 'UCS-2' && ' (emoji/special characters: 70 chars per segment)'} (sent
                  as-typed; a "Reply STOP" line is appended only if you don't include one)
                </span>
                {fieldErrors.bodyText && <span className="text-red-500">{fieldErrors.bodyText}</span>}
              </div>
              {composedSms && (
                <p className="mt-2 rounded-md bg-muted px-3 py-2 text-xs text-foreground">
                  <span className="font-medium">Preview:</span> {composedSms}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="campaign-subject">
                  Subject <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="campaign-subject"
                  className="mt-1"
                  type="text"
                  value={form.subject}
                  onChange={(e) => set('subject', e.target.value)}
                  placeholder="Hi {{firstName}}, your reward is here"
                  error={!!fieldErrors.subject}
                  aria-invalid={!!fieldErrors.subject}
                />
                {fieldErrors.subject && <p className="mt-1 text-xs text-red-500">{fieldErrors.subject}</p>}
              </div>
              <div>
                <Label htmlFor="campaign-html-body">
                  HTML body <span className="text-red-500">*</span>
                </Label>
                <textarea
                  id="campaign-html-body"
                  value={form.bodyHtml}
                  onChange={(e) => set('bodyHtml', e.target.value)}
                  rows={8}
                  placeholder="<p>Hi {{firstName}},</p><p>Save 5% off your next visit.</p>"
                  className={`mt-1 font-mono ${fieldErrors.bodyHtml ? textareaError : textareaNormal}`}
                />
                {fieldErrors.bodyHtml && <p className="mt-1 text-xs text-red-500">{fieldErrors.bodyHtml}</p>}
              </div>
            </div>
          )}
        </FormSection>

        {/* ── 3 · delivery ─────────────────────────────────────────── */}
        <FormSection num={3} title="Delivery">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            {canSend && (
              <>
                <PickerCard
                  selected={form.delivery === 'now'}
                  onClick={() => set('delivery', 'now')}
                  icon={Send}
                  label="Send now"
                  description="Deliver immediately (you'll confirm)"
                />
                <PickerCard
                  selected={form.delivery === 'later'}
                  onClick={() => set('delivery', 'later')}
                  icon={CalendarClock}
                  label="Schedule"
                  description="Pace across a time window"
                />
              </>
            )}
            <PickerCard
              selected={form.delivery === 'draft'}
              onClick={() => set('delivery', 'draft')}
              icon={FileText}
              label="Save as draft"
              description="Send later from the list"
            />
          </div>

          {form.delivery === 'later' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="campaign-timezone">Timezone</Label>
                <select
                  id="campaign-timezone"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                  className={`mt-1 ${inputNormal}`}
                >
                  {SCHEDULE_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  The times below are interpreted in this zone (default Pacific). Handles PST/PDT automatically — you
                  don't need to adjust for daylight saving.
                </p>
              </div>
              <div>
                <Label htmlFor="campaign-start">
                  Start <span className="text-red-500">*</span>
                </Label>
                <DatePicker
                  mode="datetime"
                  portal
                  timezone={form.timezone}
                  format="MM/DD/YYYY HH:mm"
                  clearable
                  className="mt-1"
                  placeholder="MM/DD/YYYY HH:mm"
                  value={zonedLocalToDate(form.scheduledAt, form.timezone)}
                  onChange={(v) =>
                    set('scheduledAt', v instanceof Date ? instantToZonedLocalInput(v, form.timezone) : '')
                  }
                  error={!!fieldErrors.scheduledAt}
                  aria-label="Start"
                />
                {fieldErrors.scheduledAt && <p className="mt-1 text-xs text-red-500">{fieldErrors.scheduledAt}</p>}
              </div>
              <div>
                <Label htmlFor="campaign-finish">
                  Finish by <span className="text-red-500">*</span>
                </Label>
                <DatePicker
                  mode="datetime"
                  portal
                  timezone={form.timezone}
                  format="MM/DD/YYYY HH:mm"
                  clearable
                  className="mt-1"
                  placeholder="MM/DD/YYYY HH:mm"
                  value={zonedLocalToDate(form.targetFinishAt, form.timezone)}
                  onChange={(v) =>
                    set('targetFinishAt', v instanceof Date ? instantToZonedLocalInput(v, form.timezone) : '')
                  }
                  error={!!fieldErrors.targetFinishAt}
                  aria-label="Finish by"
                />
                {fieldErrors.targetFinishAt && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.targetFinishAt}</p>
                )}
              </div>
              {(scheduleStartIso || scheduleFinishIso) && (
                <div className="rounded-md bg-muted px-3 py-2 text-xs text-foreground sm:col-span-2">
                  {scheduleStartIso && (
                    <div>
                      <span className="font-medium">Starts:</span> {formatInTz(scheduleStartIso, form.timezone)}
                      <span className="text-muted-foreground">
                        {' '}
                        · server (UTC): {formatInTz(scheduleStartIso, 'UTC')}
                      </span>
                    </div>
                  )}
                  {scheduleFinishIso && (
                    <div>
                      <span className="font-medium">Finishes by:</span> {formatInTz(scheduleFinishIso, form.timezone)}
                      <span className="text-muted-foreground">
                        {' '}
                        · server (UTC): {formatInTz(scheduleFinishIso, 'UTC')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground sm:col-span-2">
                The send worker paces messages across this window. Example: 35,000 recipients from 8 AM to 1 PM →
                ~117/min. Auto-throttled to the provider's per-second cap.
              </p>
            </div>
          )}
        </FormSection>
      </form>
    </Modal>
  );
}

// A numbered, titled group of fields — the same visual "spine" used by the
// Create Customer, Coupon, Reward Program, and Promotion dialogs, so they all
// read as one design system. Purely presentational — no state, no logic.
function FormSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold leading-none text-primary">
          {num}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

// Selectable card matching the Customer Type / Discount type pickers in the
// other customer-engagement dialogs. Replaces a native <select>/<radio> for the
// channel, audience, and delivery choices. Purely presentational.
function PickerCard({
  selected,
  onClick,
  icon: Icon,
  label,
  description,
}: {
  selected: boolean;
  onClick: () => void;
  icon: LucideIcon;
  label: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3 text-left transition',
        selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}
